import {
  channelLabel,
  decimalStringSchema,
  ORDER_CHANNELS,
  storeCodeSchema,
  type OperationDetail,
  type OperationInput,
  type OperationPreview,
} from '@flux/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import type { JSX } from 'react';
import { Controller, useFieldArray, useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { useReportDirty } from '@/components/forms/unsaved-changes-guard.js';
import { PageSection } from '@/components/layout/page.js';
import { Button } from '@/components/ui/button.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { Switch } from '@/components/ui/switch.js';
import { useStoresList } from '@/features/stores/use-stores-list.js';
import { OperationPreviewPanel } from './operation-preview-panel.js';
import { SubmitErrorBanner } from './operation-ui.js';
import { OperationProductsTable } from './operation-products-table.js';
import { TagChipInput } from './tag-chip-input.js';

/**
 * Formulaire d'une opération commerciale (`02` §10, refonte du 10/09/2026) :
 * nom, activation, période (fin facultative), origines, conditions (canal,
 * SKU présent, fourchette de montant, codes promo) et l'unique action —
 * ajouter un ou plusieurs produits.
 *
 * Schéma local aplati, dérivé d'`operationInputSchema` (`@flux/shared`) :
 * `periodStart`/`periodEnd` restent de simples chaînes issues d'un
 * `<input type="datetime-local">` (heure murale locale, sans fuseau) jusqu'à
 * la conversion `new Date(value).toISOString()` faite à la soumission —
 * `isoDateTimeSchema` attend un instant ISO complet, incompatible avec la
 * valeur brute du champ.
 */
const operationFormSchema = z
  .object({
    name: z.string().min(1, 'Nom requis.'),
    enabled: z.boolean(),
    periodStart: z.string().min(1, 'Date de début requise.'),
    /** Vide = « Sans date de fin ». */
    periodEnd: z.string(),
    stores: z.array(storeCodeSchema),
    channels: z.array(z.string().min(1)),
    skus: z.array(z.string().min(1)),
    minAmount: decimalStringSchema,
    /** Vide = pas de plafond. */
    maxAmount: z.string(),
    discountCodes: z.array(z.string().min(1)),
    products: z.array(
      z.object({
        sku: z.string().trim().min(1, 'SKU requis.'),
        qty: z.coerce.number(),
        unitPrice: z.string(),
      }),
    ),
  })
  .superRefine((values, ctx) => {
    if (
      values.periodEnd !== '' &&
      new Date(values.periodEnd).getTime() <= new Date(values.periodStart).getTime()
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['periodEnd'],
        message: 'La date de fin doit être postérieure à la date de début.',
      });
    }
    const seenSkus = new Set<string>();
    values.products.forEach((product, index) => {
      if (!Number.isInteger(product.qty) || product.qty <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['products', index, 'qty'],
          message: 'Quantité entière positive requise.',
        });
      }
      if (!/^-?\d+(\.\d+)?$/.test(product.unitPrice)) {
        ctx.addIssue({
          code: 'custom',
          path: ['products', index, 'unitPrice'],
          message: 'Prix invalide (ex. 12.50).',
        });
      }
      if (product.sku !== '' && seenSkus.has(product.sku)) {
        ctx.addIssue({
          code: 'custom',
          path: ['products', index, 'sku'],
          message: 'SKU déjà ajouté par cette opération.',
        });
      }
      seenSkus.add(product.sku);
    });
    if (values.maxAmount !== '' && !/^-?\d+(\.\d+)?$/.test(values.maxAmount)) {
      ctx.addIssue({
        code: 'custom',
        path: ['maxAmount'],
        message: 'Montant invalide (ex. 100.00).',
      });
    }
  });

export type OperationFormValues = z.infer<typeof operationFormSchema>;

/** `YYYY-MM-DDTHH:mm` en heure locale du navigateur — symétrique de `new Date(value).toISOString()`. */
function toDatetimeLocalValue(iso: string): string {
  const date = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function defaultValuesFor(detail: OperationDetail | undefined): OperationFormValues {
  if (!detail) {
    return {
      name: '',
      enabled: true,
      periodStart: '',
      periodEnd: '',
      stores: [],
      channels: [],
      skus: [],
      minAmount: '0',
      maxAmount: '',
      discountCodes: [],
      products: [{ sku: '', qty: 1, unitPrice: '0' }],
    };
  }
  return {
    name: detail.name,
    enabled: detail.enabled,
    periodStart: toDatetimeLocalValue(detail.period.start),
    periodEnd: detail.period.end === null ? '' : toDatetimeLocalValue(detail.period.end),
    stores: [...detail.stores],
    channels: [...detail.conditions.channels],
    skus: [...detail.conditions.skus],
    minAmount: detail.conditions.minAmount,
    maxAmount: detail.conditions.maxAmount ?? '',
    discountCodes: [...detail.conditions.discountCodes],
    products: detail.products.map((product) => ({ ...product })),
  };
}

export function toOperationInput(values: OperationFormValues): OperationInput {
  return {
    name: values.name,
    enabled: values.enabled,
    period: {
      start: new Date(values.periodStart).toISOString(),
      end: values.periodEnd === '' ? null : new Date(values.periodEnd).toISOString(),
      timezone: 'Europe/Paris',
    },
    stores: values.stores,
    conditions: {
      channels: values.channels,
      skus: values.skus,
      minAmount: values.minAmount,
      maxAmount: values.maxAmount === '' ? null : values.maxAmount,
      discountCodes: values.discountCodes,
    },
    products: values.products.map((product) => ({
      sku: product.sku,
      qty: product.qty,
      unitPrice: product.unitPrice,
    })),
  };
}

export interface OperationFormProps {
  detail: OperationDetail | undefined;
  isSaving: boolean;
  onSubmit: (input: OperationInput) => void;
  onPreview: (input: OperationInput) => void;
  preview: OperationPreview | undefined;
  isPreviewing: boolean;
  isPreviewError: boolean;
  previewErrorMessage?: string | undefined;
  /** Absent en création : une opération n'existant pas encore ne s'archive pas. */
  onArchive?: (() => void) | undefined;
  /** Échec du dernier enregistrement, affiché en tête du formulaire (la saisie est conservée). */
  submitError?: string | null | undefined;
  /** Remonte l'état modifié à la page (garde de sortie). */
  onDirtyChange?: ((dirty: boolean) => void) | undefined;
}

export function OperationForm({
  detail,
  isSaving,
  onSubmit,
  onPreview,
  preview,
  isPreviewing,
  isPreviewError,
  previewErrorMessage,
  onArchive,
  submitError = null,
  onDirtyChange,
}: OperationFormProps): JSX.Element {
  const { data: storesPage } = useStoresList();
  const stores = storesPage?.items ?? [];

  // `values` (et non `defaultValues`) : quand la fiche enregistrée change, le
  // formulaire repart de cette version — « Enregistrer » redevient inactif,
  // preuve que la sauvegarde a abouti. RHF ne réinitialise que si la valeur
  // diffère réellement (comparaison profonde).
  const form = useForm<OperationFormValues>({
    resolver: zodResolver(operationFormSchema) as Resolver<OperationFormValues>,
    values: defaultValuesFor(detail),
  });
  const isNew = detail === undefined;
  // Archivée : plus rien ne s'enregistre (la page affiche la bannière « Opération archivée »).
  const readOnly = detail?.archived === true;

  const productsFieldArray = useFieldArray({ control: form.control, name: 'products' });
  const values = form.watch();
  const { errors, isDirty } = form.formState;
  const noEndDate = values.periodEnd === '';
  useReportDirty(isDirty, onDirtyChange);

  // L'erreur « fin postérieure au début » porte sur deux champs : une fois
  // affichée (décochage de « Sans date de fin », soumission), elle doit être
  // réévaluée à chaque modification de l'un ou l'autre — en mode `onSubmit`,
  // react-hook-form ne revalide rien avant la première soumission.
  const revalidatePeriodEnd = (): void => {
    if (form.getValues('periodEnd') !== '') {
      void form.trigger('periodEnd');
    }
  };

  return (
    <form
      className="flex w-full max-w-[880px] flex-col gap-6"
      noValidate
      onSubmit={(event) => {
        void form.handleSubmit((submitted) => {
          onSubmit(toOperationInput(submitted));
        })(event);
      }}
    >
      <SubmitErrorBanner message={submitError} />
      <fieldset disabled={readOnly} className="flex min-w-0 flex-col gap-6">
      <PageSection>
        <div className="flex flex-col gap-5">
          <Field label="Nom" htmlFor="operation-name" error={errors.name?.message}>
            <Input
              id="operation-name"
              aria-invalid={errors.name ? true : undefined}
              {...form.register('name')}
            />
          </Field>

          <Controller
            control={form.control}
            name="enabled"
            render={({ field }) => (
              <label className="flex w-fit items-center gap-2 text-sm">
                <Switch checked={field.value} onCheckedChange={field.onChange} />
                Actif
              </label>
            )}
          />

          <div className="flex flex-col gap-3 border-t border-border pt-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Début" htmlFor="operation-start" error={errors.periodStart?.message}>
                <Input
                  id="operation-start"
                  type="datetime-local"
                  aria-invalid={errors.periodStart ? true : undefined}
                  {...form.register('periodStart', { onChange: revalidatePeriodEnd })}
                />
              </Field>
              <Field label="Fin" htmlFor="operation-end" error={errors.periodEnd?.message}>
                <Input
                  id="operation-end"
                  type="datetime-local"
                  disabled={noEndDate}
                  aria-invalid={errors.periodEnd ? true : undefined}
                  {...form.register('periodEnd', { onChange: revalidatePeriodEnd })}
                />
              </Field>
            </div>
            <label className="flex w-fit items-center gap-2 text-sm">
              <Checkbox
                checked={noEndDate}
                onCheckedChange={(checked) => {
                  form.setValue(
                    'periodEnd',
                    checked === true ? '' : toDatetimeLocalValue(new Date().toISOString()),
                    // `shouldDirty` : sans lui, cocher la case ne compte pas comme
                    // une modification et « Enregistrer » reste inactif.
                    { shouldValidate: true, shouldDirty: true },
                  );
                }}
              />
              Sans date de fin
            </label>
          </div>

          <div className="border-t border-border pt-5">
            <Controller
              control={form.control}
              name="stores"
              render={({ field }) => (
                <fieldset className="flex min-w-0 flex-col gap-2">
                  <legend className="mb-0.5 text-sm font-medium">Origines</legend>
                  <p className="text-xs text-muted-foreground">
                    Aucune case cochée = toutes les origines.
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {stores.map((store) => (
                      <label key={store.code} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={field.value.includes(store.code)}
                          onCheckedChange={(checked) => {
                            field.onChange(
                              checked === true
                                ? [...field.value, store.code]
                                : field.value.filter((code) => code !== store.code),
                            );
                          }}
                        />
                        {store.name}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            />
          </div>
        </div>
      </PageSection>

      <PageSection
        title="Conditions"
        description="Toutes les conditions renseignées doivent être remplies. Une condition vide ne filtre rien."
      >
        <div className="flex flex-col gap-5">
          <Controller
            control={form.control}
            name="channels"
            render={({ field }) => (
              <Field label="Canal">
                <TagChipInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="web, tiktok, subscription_*…"
                  aria-label="Canaux"
                />
                <div className="flex flex-wrap gap-1.5">
                  {ORDER_CHANNELS.filter((code) => !field.value.includes(code)).map((code) => (
                    <Button
                      key={code}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        field.onChange([...field.value, code]);
                      }}
                    >
                      + {channelLabel(code)}
                    </Button>
                  ))}
                </div>
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="skus"
            render={({ field }) => (
              <Field label="SKU présent dans la commande">
                <TagChipInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="SKU-MASQUE-75…"
                  aria-label="SKU présents"
                />
              </Field>
            )}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Montant TTC minimum" htmlFor="operation-min-amount">
              <Input id="operation-min-amount" {...form.register('minAmount')} />
            </Field>
            <Field
              label="Montant TTC maximum"
              htmlFor="operation-max-amount"
              error={errors.maxAmount?.message}
            >
              <Input
                id="operation-max-amount"
                placeholder="sans limite"
                aria-invalid={errors.maxAmount ? true : undefined}
                {...form.register('maxAmount')}
              />
            </Field>
          </div>

          <Controller
            control={form.control}
            name="discountCodes"
            render={({ field }) => (
              <Field label="Codes promo">
                <TagChipInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="NOEL25…"
                  aria-label="Codes promo"
                />
              </Field>
            )}
          />
        </div>
      </PageSection>

      <OperationProductsTable
        register={form.register}
        fieldArray={productsFieldArray}
        errors={errors}
      />
      </fieldset>

      <OperationPreviewPanel
        preview={preview}
        isLoading={isPreviewing}
        isError={isPreviewError}
        errorMessage={previewErrorMessage}
        onRun={() => {
          onPreview(toOperationInput(form.getValues()));
        }}
      />

      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-2">
          {onArchive ? (
            <Button type="button" variant="outline" onClick={onArchive}>
              Archiver…
            </Button>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <SaveButton isLoading={isSaving} isDirty={isNew || isDirty} />
          </div>
        </div>
      )}
    </form>
  );
}
